import { useTranslation } from 'react-i18next'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { StudyFiguresPopulationSource } from '@digital-twins/shared-types'
import { QuestionCardFigures } from './useQuestionCardFigures'
import { FigureDimension, FigureDimensionGroup } from './figureDimensions'

/**
 * The survey tab's segment picker for a figure-drawn card (plots plan D3):
 * today's dropdown, listing the figure service's cuts in today's groups and
 * order (Segments, Questions, Population, Assets), plus the value select a
 * lane-merged card filters by. When the list of cuts could not be read, the
 * control says so and offers a retry instead of quietly dropping groups.
 */

const GROUPS = [
  { group: 'segment', titleKey: 'studyReport.questionCard.segmentGroupSegments', testId: 'segment-group-segments' },
  { group: 'survey', titleKey: 'studyReport.questionCard.segmentGroupQuestions', testId: 'segment-group-questions' },
  { group: 'population', titleKey: 'studyReport.questionCard.segmentGroupPopulation', testId: 'segment-group-population' },
  { group: 'asset', titleKey: 'studyReport.questionCard.segmentGroupAssets', testId: 'segment-group-assets' },
] as const satisfies ReadonlyArray<{ group: FigureDimensionGroup; titleKey: string; testId: string }>

const NONE = 'none'

/** The note the Population group shows for each source that lists no cut. */
const POPULATION_NOTES = {
  none: { textKey: 'studyReport.questionCard.segmentPopulationNone', testId: 'segment-population-none' },
  none_formable: {
    textKey: 'studyReport.questionCard.segmentPopulationNoneFormable',
    testId: 'segment-population-none-formable',
  },
  unavailable: {
    textKey: 'studyReport.questionCard.segmentPopulationUnavailable',
    testId: 'segment-population-unavailable',
  },
} as const satisfies Partial<Record<StudyFiguresPopulationSource, { textKey: string; testId: string }>>

type PopulationNote = (typeof POPULATION_NOTES)[keyof typeof POPULATION_NOTES]

function populationNoteOf(source: StudyFiguresPopulationSource | null): PopulationNote | undefined {
  return source && source in POPULATION_NOTES ? POPULATION_NOTES[source as keyof typeof POPULATION_NOTES] : undefined
}

function optionTestId(dimension: FigureDimension): string {
  return `segment-option-${dimension.key.replace(/:/g, '-')}`
}

interface FigureSegmentControlsProps {
  figures: QuestionCardFigures
}

export function FigureSegmentControls(props: FigureSegmentControlsProps) {
  const { figures } = props

  const { t } = useTranslation('app')

  if (figures.metaStatus === 'failed') {
    return (
      <div
        className="inline-flex items-center gap-2 px-2.5 py-0.5 bg-[hsl(var(--report-surface))] rounded-full text-xs text-muted-foreground"
        data-testid="question-card-segments-unavailable"
      >
        <span>{t('studyReport.questionCard.segmentsUnavailable')}</span>
        <button
          type="button"
          className="font-medium text-foreground hover:underline"
          onClick={figures.retryMeta}
          data-testid="question-card-segments-retry"
        >
          {t('studyReport.retryGeneration')}
        </button>
      </div>
    )
  }

  // The Population group says why it lists no cut (never falling back to other
  // attributes): no curated columns, none that splits these answers, or a
  // population that could not be read.
  const populationNote = populationNoteOf(figures.populationSource)
  const listed = GROUPS.map((entry) => ({
    ...entry,
    dimensions: figures.dimensions.filter((dimension) => dimension.group === entry.group),
    note:
      entry.group === 'population' && populationNote
        ? { text: t(populationNote.textKey), testId: populationNote.testId }
        : null,
  })).filter((entry) => entry.dimensions.length > 0 || entry.note)
  if (!listed.some((entry) => entry.dimensions.length > 0)) {
    return null
  }

  const valueSelect =
    figures.activeDimension && figures.valueOptions.length > 0 ? (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[hsl(var(--report-surface))] rounded-full text-xs">
        <span className="text-foreground/70">{t('studyReport.questionCard.segmentValue')}</span>
        <Select value={figures.activeValueId ?? undefined} onValueChange={figures.selectValue}>
          <SelectTrigger
            data-testid="question-card-segment-value-select"
            className="h-auto border-none bg-transparent p-0 text-xs font-medium shadow-none focus:ring-0 w-auto min-w-[80px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="min-w-[220px] max-h-[300px]">
            {figures.valueOptions.map((option) => (
              <SelectItem key={option.id} value={option.id} data-testid={`segment-value-option-${option.id}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null

  return (
    <>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[hsl(var(--report-surface))] rounded-full text-xs">
        <span className="text-foreground/70">{t('studyReport.questionCard.segmentLabel')}</span>
        <Select
          value={figures.activeDimension?.key ?? NONE}
          onValueChange={(value) => figures.selectDimension(value === NONE ? null : value)}
        >
          <SelectTrigger
            data-testid="question-card-segment-select"
            className="h-auto border-none bg-transparent p-0 text-xs font-medium shadow-none focus:ring-0 w-auto min-w-[100px]"
          >
            <SelectValue placeholder={t('studyReport.questionCard.segmentAllRespondents')} />
          </SelectTrigger>
          <SelectContent className="min-w-[360px] max-h-[300px]">
            <SelectItem value={NONE} data-testid="segment-option-all-respondents">
              {t('studyReport.questionCard.segmentAllRespondents')}
            </SelectItem>
            {listed.map((entry, index) => (
              <div key={entry.group}>
                <div
                  className={cn(
                    'px-2 py-1.5 text-xs font-medium text-muted-foreground',
                    index > 0 && 'border-t mt-1 pt-2',
                  )}
                  data-testid={entry.testId}
                >
                  {t(entry.titleKey)}
                </div>
                {entry.dimensions.map((dimension) => (
                  <SelectItem key={dimension.key} value={dimension.key} data-testid={optionTestId(dimension)}>
                    {dimension.label}
                  </SelectItem>
                ))}
                {entry.note && (
                  <p className="px-2 pb-1.5 text-xs text-muted-foreground" data-testid={entry.note.testId}>
                    {entry.note.text}
                  </p>
                )}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>
      {valueSelect}
    </>
  )
}
